import { assertCommentWriteAccess, assertAdminOnlyFlagWriteAccess, assertAdminOnlyRowAccess, assertMeetingNoteWriteAccess, canSeeAdminOnlyContent, assertTaskWriteAccess, replaceMeetingNoteMentions, replaceMeetingNoteTagAssignments, toZeroDate, } from "../projects-helpers.js";
function authorizedTasksQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.tasks;
    }
    const personID = ctx.personID;
    return personID
        ? projectZql.tasks.whereExists("assignees", (assignee) => assignee.where("personID", personID))
        : projectZql.tasks.where("id", "__unauthorized__");
}
function tasksWithRelations(ctx, projectZql) {
    return authorizedTasksQuery(ctx, projectZql)
        .related("assignees", (assignee) => assignee.related("person"))
        .related("projects", (taskProject) => taskProject.related("project"))
        .related("projectGroups", (taskProjectGroup) => taskProjectGroup.related("projectGroup"));
}
function authorizedNotificationsQuery(ctx, projectZql) {
    return ctx.userID
        ? projectZql.notifications.where("userID", ctx.userID)
        : projectZql.notifications.where("id", "__unauthorized__");
}
const taskStateLabels = {
    backlog: "Backlog",
    todo: "To do",
    in_progress: "In progress",
    done: "Done",
};
function zeroDateLabel(value) {
    return value == null
        ? "No due date"
        : new Date(value).toISOString().slice(0, 10);
}
export function describeTaskUpdate(task, args) {
    const changes = [];
    if (args.title !== undefined && args.title !== task.title) {
        changes.push(`Title: “${task.title}” → “${args.title}”`);
    }
    if (args.description !== undefined &&
        args.description !== (task.description ?? null)) {
        const before = task.description?.trim();
        const after = args.description?.trim();
        changes.push(!before
            ? "Description added"
            : !after
                ? "Description removed"
                : "Description changed");
    }
    if (args.state !== undefined && args.state !== task.state) {
        changes.push(`State: ${taskStateLabels[task.state]} → ${taskStateLabels[args.state]}`);
    }
    if (args.dueDate !== undefined) {
        const nextDueDate = toZeroDate(args.dueDate);
        if (nextDueDate !== (task.dueDate ?? null)) {
            changes.push(`Due date: ${zeroDateLabel(task.dueDate)} → ${zeroDateLabel(nextDueDate)}`);
        }
    }
    return changes.join("; ");
}
async function notifyTaskUpdate({ tx, ctx, projectZql, task, args, }) {
    if (!ctx.userID)
        return;
    const body = describeTaskUpdate(task, args);
    if (!body)
        return;
    const updater = await tx.run(projectZql.people.where("userID", ctx.userID).one());
    const updaterName = updater
        ? `${updater.firstName} ${updater.lastName}`.trim()
        : "Someone";
    const updatedTitle = args.title ?? task.title;
    const recipientUserIDs = new Set((task.assignees ?? [])
        .map((assignee) => assignee.person?.userID)
        .filter((userID) => !!userID && userID !== ctx.userID));
    for (const userID of recipientUserIDs) {
        await tx.mutate.notifications.insert({
            id: crypto.randomUUID(),
            userID,
            type: "task_updated",
            title: `${updaterName} updated “${updatedTitle}”`,
            body,
            entityType: "task",
            entityID: task.id,
            sourceTaskID: task.id,
            actorID: ctx.userID,
            isRead: false,
        });
    }
}
async function notifyTaskAssignee({ tx, ctx, projectZql, taskID, personID, }) {
    const assignee = await tx.run(projectZql.people.where("id", personID).one());
    const assigneeUserID = assignee?.userID;
    if (!assigneeUserID || assigneeUserID === ctx.userID)
        return;
    const task = await tx.run(projectZql.tasks.where("id", taskID).one());
    if (!task)
        return;
    const assigner = ctx.userID
        ? await tx.run(projectZql.people.where("userID", ctx.userID).one())
        : null;
    const assignerName = assigner
        ? `${assigner.firstName} ${assigner.lastName}`.trim()
        : "";
    const title = `${assignerName || "Someone"} assigned you a task`;
    const existingNotification = await tx.run(projectZql.notifications
        .where("userID", assigneeUserID)
        .where("sourceTaskID", taskID)
        .where("type", "task_assigned")
        .one());
    if (existingNotification) {
        await tx.mutate.notifications.update({
            id: existingNotification.id,
            title,
            body: task.title,
            entityType: "task",
            entityID: task.id,
            sourceTaskID: task.id,
            actorID: ctx.userID ?? null,
            isRead: false,
        });
        return;
    }
    await tx.mutate.notifications.insert({
        id: crypto.randomUUID(),
        userID: assigneeUserID,
        type: "task_assigned",
        title,
        body: task.title,
        entityType: "task",
        entityID: task.id,
        sourceTaskID: task.id,
        actorID: ctx.userID ?? null,
        isRead: false,
    });
}
function authorizedCommentsQuery(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx)) {
        return projectZql.comments;
    }
    // Comments on managers-only meeting notes are as restricted as the note.
    if (ctx.role === "employee") {
        return projectZql.comments.where(({ cmp, exists, or }) => or(cmp("entityType", "!=", "meeting_note"), exists("meetingNote", (note) => note.where("adminOnly", false))));
    }
    return projectZql.comments.where("id", "__unauthorized__");
}
function commentsWithRelations(ctx, projectZql) {
    return authorizedCommentsQuery(ctx, projectZql)
        .related("author")
        .related("resolver")
        .related("mentions", (mention) => mention.related("people"));
}
/** Rejects comment writes on managers-only meeting notes for non-admins. */
async function assertCommentEntityAccess(tx, ctx, projectZql, entity) {
    if (entity?.entityType !== "meeting_note" || !entity.entityID)
        return;
    assertAdminOnlyRowAccess(ctx, await tx.run(projectZql.meetingNotes.where("id", entity.entityID).one()));
}
function authorizedMeetingNotesQuery(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx)) {
        return projectZql.meetingNotes;
    }
    if (ctx.role === "employee") {
        return projectZql.meetingNotes.where("adminOnly", false);
    }
    return projectZql.meetingNotes.where("id", "__unauthorized__");
}
function meetingNotesWithRelations(ctx, projectZql) {
    return authorizedMeetingNotesQuery(ctx, projectZql)
        .related("document")
        .related("tagAssignments", (assignment) => assignment.related("tag"))
        .related("mentions", (mention) => mention.related("people"));
}
function meetingNotesWithContext(ctx, projectZql) {
    return meetingNotesWithRelations(ctx, projectZql)
        .related("project")
        .related("projectGroup");
}
function authorizedMeetingNoteTagsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.meetingNoteTags;
    }
    return projectZql.meetingNoteTags.where("id", "__unauthorized__");
}
export function createCollaborationQueries({ defineProjectQuery, projectZql, }) {
    return {
        tasks: {
            list: defineProjectQuery(({ ctx }) => tasksWithRelations(ctx, projectZql).orderBy("createdAt", "desc")),
            byID: defineProjectQuery(({ args, ctx }) => tasksWithRelations(ctx, projectZql).where("id", args.id).one()),
        },
        notifications: {
            listMine: defineProjectQuery(({ ctx }) => authorizedNotificationsQuery(ctx, projectZql)
                .orderBy("createdAt", "desc")
                .limit(50)),
        },
        comments: {
            byEntity: defineProjectQuery(({ args, ctx, }) => commentsWithRelations(ctx, projectZql)
                .where("entityType", args.entityType)
                .where("entityID", args.entityID)
                .orderBy("createdAt", "asc")),
        },
        meetingNotes: {
            commandItems: defineProjectQuery(({ ctx }) => authorizedMeetingNotesQuery(ctx, projectZql).orderBy("meetingDate", "desc")),
            all: defineProjectQuery(({ ctx }) => meetingNotesWithContext(ctx, projectZql).orderBy("meetingDate", "desc")),
            byID: defineProjectQuery(({ args, ctx }) => meetingNotesWithContext(ctx, projectZql).where("id", args.id).one()),
            byProject: defineProjectQuery(({ args, ctx }) => meetingNotesWithRelations(ctx, projectZql)
                .where("projectID", args.projectID)
                .orderBy("meetingDate", "desc")),
            byProjectGroup: defineProjectQuery(({ args, ctx, }) => meetingNotesWithRelations(ctx, projectZql)
                .where("projectGroupID", args.projectGroupID)
                .orderBy("meetingDate", "desc")),
            byMentionedProject: defineProjectQuery(({ args, ctx }) => meetingNotesWithContext(ctx, projectZql)
                .whereExists("projectMentions", (mention) => mention.where("projectID", args.projectID))
                .orderBy("meetingDate", "desc")),
        },
        meetingNoteTags: {
            list: defineProjectQuery(({ ctx }) => authorizedMeetingNoteTagsQuery(ctx, projectZql).orderBy("name", "asc")),
        },
    };
}
export function createCollaborationMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createTaskInputSchema, updateTaskInputSchema, taskIDInputSchema, taskAssigneeInputSchema, taskProjectInputSchema, taskProjectGroupInputSchema, markNotificationReadInputSchema, markAllNotificationsReadInputSchema, createCommentInputSchema, commentIDInputSchema, createMeetingNoteInputSchema, updateMeetingNoteInputSchema, meetingNoteIDInputSchema, createMeetingNoteTagInputSchema, updateMeetingNoteTagInputSchema, meetingNoteTagIDInputSchema, } = schemas;
    return {
        tasks: {
            create: defineProjectMutator(createTaskInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                await tx.mutate.tasks.insert({
                    id: args.id,
                    title: args.title,
                    description: args.description ?? null,
                    state: args.state ?? "backlog",
                    dueDate: toZeroDate(args.dueDate),
                });
            }),
            update: defineProjectMutator(updateTaskInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const task = await tx.run(projectZql.tasks
                    .where("id", args.id)
                    .related("assignees", (assignee) => assignee.related("person"))
                    .one());
                if (!task)
                    return;
                await tx.mutate.tasks.update({
                    id: args.id,
                    ...(args.title !== undefined ? { title: args.title } : {}),
                    ...(args.description !== undefined
                        ? { description: args.description ?? null }
                        : {}),
                    ...(args.state !== undefined ? { state: args.state } : {}),
                    ...(args.dueDate !== undefined
                        ? { dueDate: toZeroDate(args.dueDate) }
                        : {}),
                });
                await notifyTaskUpdate({ tx, ctx, projectZql, task, args });
            }),
            delete: defineProjectMutator(taskIDInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                await tx.mutate.tasks.delete({ id: args.id });
            }),
        },
        taskAssignees: {
            add: defineProjectMutator(taskAssigneeInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskAssignees
                    .where("taskID", args.taskID)
                    .where("personID", args.personID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.taskAssignees.insert(args);
                await notifyTaskAssignee({
                    tx,
                    ctx,
                    projectZql,
                    taskID: args.taskID,
                    personID: args.personID,
                });
            }),
            remove: defineProjectMutator(taskAssigneeInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskAssignees
                    .where("taskID", args.taskID)
                    .where("personID", args.personID)
                    .one());
                if (existing)
                    await tx.mutate.taskAssignees.delete({ id: existing.id });
            }),
        },
        taskProjects: {
            add: defineProjectMutator(taskProjectInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskProjects
                    .where("taskID", args.taskID)
                    .where("projectID", args.projectID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.taskProjects.insert(args);
            }),
            remove: defineProjectMutator(taskProjectInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskProjects
                    .where("taskID", args.taskID)
                    .where("projectID", args.projectID)
                    .one());
                if (existing)
                    await tx.mutate.taskProjects.delete({ id: existing.id });
            }),
        },
        taskProjectGroups: {
            add: defineProjectMutator(taskProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskProjectGroups
                    .where("taskID", args.taskID)
                    .where("projectGroupID", args.projectGroupID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.taskProjectGroups.insert(args);
            }),
            remove: defineProjectMutator(taskProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskProjectGroups
                    .where("taskID", args.taskID)
                    .where("projectGroupID", args.projectGroupID)
                    .one());
                if (existing) {
                    await tx.mutate.taskProjectGroups.delete({ id: existing.id });
                }
            }),
        },
        notifications: {
            markRead: defineProjectMutator(markNotificationReadInputSchema, async ({ tx, ctx, args, }) => {
                const notification = await tx.run(authorizedNotificationsQuery(ctx, projectZql)
                    .where("id", args.id)
                    .one());
                if (!notification) {
                    throw new Error("Notification not found");
                }
                await tx.mutate.notifications.update({
                    id: args.id,
                    isRead: true,
                });
            }),
            markAllRead: defineProjectMutator(markAllNotificationsReadInputSchema, async ({ tx, ctx }) => {
                const unread = await tx.run(authorizedNotificationsQuery(ctx, projectZql).where("isRead", false));
                for (const notification of unread ?? []) {
                    await tx.mutate.notifications.update({
                        id: notification.id,
                        isRead: true,
                    });
                }
            }),
        },
        comments: {
            create: defineProjectMutator(createCommentInputSchema, async ({ tx, ctx, args, }) => {
                assertCommentWriteAccess(ctx);
                if (!ctx.userID) {
                    throw new Error("Not allowed to create comments");
                }
                await assertCommentEntityAccess(tx, ctx, projectZql, args);
                const now = Date.now();
                await tx.mutate.comments.insert({
                    id: args.id,
                    entityType: args.entityType,
                    entityID: args.entityID,
                    parentID: args.parentID ?? null,
                    content: args.content,
                    authorID: ctx.userID,
                    isResolved: false,
                    resolvedBy: null,
                    resolvedAt: null,
                    createdAt: now,
                    updatedAt: now,
                });
                for (const personID of args.mentionedPersonIDs ?? []) {
                    await tx.mutate.commentMentions.insert({
                        id: crypto.randomUUID(),
                        commentID: args.id,
                        personID,
                    });
                }
            }),
            delete: defineProjectMutator(commentIDInputSchema, async ({ tx, ctx, args, }) => {
                const comment = await tx.run(authorizedCommentsQuery(ctx, projectZql).where("id", args.id).one());
                if (!comment) {
                    throw new Error("Comment not found");
                }
                if (comment.authorID !== ctx.userID) {
                    throw new Error("Not allowed to delete this comment");
                }
                await tx.mutate.comments.delete({ id: args.id });
            }),
            resolve: defineProjectMutator(commentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertCommentWriteAccess(ctx);
                if (!ctx.userID) {
                    throw new Error("Not allowed to resolve comments");
                }
                await assertCommentEntityAccess(tx, ctx, projectZql, await tx.run(projectZql.comments.where("id", args.id).one()));
                await tx.mutate.comments.update({
                    id: args.id,
                    isResolved: true,
                    resolvedBy: ctx.userID,
                    resolvedAt: Date.now(),
                });
            }),
            unresolve: defineProjectMutator(commentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertCommentWriteAccess(ctx);
                await assertCommentEntityAccess(tx, ctx, projectZql, await tx.run(projectZql.comments.where("id", args.id).one()));
                await tx.mutate.comments.update({
                    id: args.id,
                    isResolved: false,
                    resolvedBy: null,
                    resolvedAt: null,
                });
            }),
        },
        meetingNotes: {
            create: defineProjectMutator(createMeetingNoteInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                if (args.adminOnly)
                    assertAdminOnlyFlagWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.documents.insert({
                    id: args.documentID,
                    createdAt: now,
                    updatedAt: now,
                });
                await tx.mutate.meetingNotes.insert({
                    id: args.id,
                    title: args.title,
                    content: args.content ?? null,
                    meetingDate: toZeroDate(args.meetingDate),
                    projectID: args.projectID ?? null,
                    projectGroupID: args.projectGroupID ?? null,
                    documentID: args.documentID,
                    adminOnly: args.adminOnly ?? false,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
                await replaceMeetingNoteTagAssignments(tx, args.id, args.tagIDs ?? []);
                await replaceMeetingNoteMentions(tx, args.id, args.mentionedPersonIDs ?? []);
            }),
            update: defineProjectMutator(updateMeetingNoteInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                if (args.adminOnly !== undefined)
                    assertAdminOnlyFlagWriteAccess(ctx);
                assertAdminOnlyRowAccess(ctx, await tx.run(projectZql.meetingNotes.where("id", args.id).one()));
                const noteUpdates = {
                    id: args.id,
                    updatedAt: Date.now(),
                };
                if (args.title !== undefined)
                    noteUpdates.title = args.title;
                if (args.content !== undefined)
                    noteUpdates.content = args.content;
                if (args.meetingDate !== undefined) {
                    noteUpdates.meetingDate = toZeroDate(args.meetingDate);
                }
                if (args.adminOnly !== undefined) {
                    noteUpdates.adminOnly = args.adminOnly;
                }
                if (Object.keys(noteUpdates).length > 2) {
                    await tx.mutate.meetingNotes.update(noteUpdates);
                }
                if (args.tagIDs !== undefined) {
                    await replaceMeetingNoteTagAssignments(tx, args.id, args.tagIDs);
                }
                if (args.mentionedPersonIDs !== undefined) {
                    await replaceMeetingNoteMentions(tx, args.id, args.mentionedPersonIDs);
                }
            }),
            delete: defineProjectMutator(meetingNoteIDInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                const note = await tx.run(projectZql.meetingNotes.where("id", args.id).one());
                assertAdminOnlyRowAccess(ctx, note);
                await tx.mutate.meetingNotes.delete({ id: args.id });
                if (note?.documentID) {
                    await tx.mutate.documents.delete({ id: note.documentID });
                }
            }),
        },
        meetingNoteTags: {
            create: defineProjectMutator(createMeetingNoteTagInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.meetingNoteTags.insert({
                    id: args.id,
                    name: args.name,
                    color: args.color ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(updateMeetingNoteTagInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                await tx.mutate.meetingNoteTags.update({
                    id: args.id,
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.color !== undefined ? { color: args.color ?? null } : {}),
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(meetingNoteTagIDInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                await tx.mutate.meetingNoteTags.delete({ id: args.id });
            }),
        },
    };
}
